import { z } from 'zod';

/**
 * Triggers start a flow or a group without anyone pressing Run: on a schedule (cron + timezone) or
 * from a webhook (a URL with a secret token, e.g. called by GitLab after a deploy).
 *
 * Schedules use standard 5-field cron ("minute hour day-of-month month day-of-week") evaluated in an
 * IANA timezone, so "every day at 02:00 Europe/Berlin" stays at 02:00 across daylight-saving changes.
 */

export const TRIGGER_KINDS = ['SCHEDULE', 'WEBHOOK'] as const;
export type TriggerKind = (typeof TRIGGER_KINDS)[number];
export const TRIGGER_TARGETS = ['FLOW', 'GROUP'] as const;
export type TriggerTarget = (typeof TRIGGER_TARGETS)[number];

/** Schedules may not fire more often than this (each run costs browsers and AI calls). */
export const MIN_SCHEDULE_GAP_MINUTES = 10;

// ---------------------------------------------------------------- cron

export class CronError extends Error {}

export interface CronSpec {
  minutes: Set<number>;
  hours: Set<number>;
  daysOfMonth: Set<number>;
  months: Set<number>;
  /** 0 = Sunday ... 6 = Saturday. */
  daysOfWeek: Set<number>;
  domAny: boolean;
  dowAny: boolean;
}

const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function parseField(raw: string, min: number, max: number, label: string, names?: string[]): Set<number> {
  const out = new Set<number>();
  const value = (s: string): number => {
    const named = names?.indexOf(s.toUpperCase()) ?? -1;
    if (named >= 0) return named + (label === 'month' ? 1 : 0);
    if (!/^\d+$/.test(s)) throw new CronError(`"${s}" is not a valid ${label}`);
    const n = Number(s);
    if (n < min || n > max) throw new CronError(`${label} ${n} is outside ${min}-${max}`);
    return n;
  };
  for (const part of raw.split(',')) {
    const [range, stepRaw] = part.split('/');
    if (!range || part.split('/').length > 2) throw new CronError(`"${part}" is not a valid ${label}`);
    const step = stepRaw === undefined ? 1 : Number(stepRaw);
    if (!Number.isInteger(step) || step < 1) throw new CronError(`"${stepRaw}" is not a valid step`);
    let lo: number;
    let hi: number;
    if (range === '*') {
      lo = min;
      hi = max;
    } else if (range.includes('-')) {
      const [a, b] = range.split('-');
      lo = value(a ?? '');
      hi = value(b ?? '');
      if (lo > hi) throw new CronError(`${label} range ${range} goes backwards`);
    } else {
      lo = value(range);
      hi = stepRaw === undefined ? lo : max;
    }
    for (let n = lo; n <= hi; n += step) out.add(n);
  }
  return out;
}

/** Parses "minute hour day-of-month month day-of-week". Throws CronError with a readable message. */
export function parseCron(expr: string): CronSpec {
  const fields = expr.trim().split(/\s+/);
  if (fields.length !== 5) throw new CronError('A schedule has 5 parts: minute, hour, day of month, month, day of week');
  const [mi, h, dom, mo, dow] = fields as [string, string, string, string, string];
  const daysOfWeek = parseField(dow, 0, 7, 'day of week', DAY_NAMES);
  if (daysOfWeek.has(7)) {
    daysOfWeek.delete(7);
    daysOfWeek.add(0);
  }
  return {
    minutes: parseField(mi, 0, 59, 'minute'),
    hours: parseField(h, 0, 23, 'hour'),
    daysOfMonth: parseField(dom, 1, 31, 'day of month'),
    months: parseField(mo, 1, 12, 'month', MONTH_NAMES),
    daysOfWeek,
    domAny: dom === '*',
    dowAny: dow === '*',
  };
}

export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const formatters = new Map<string, Intl.DateTimeFormat>();
interface Wall {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
}
/** Wall-clock time of an instant in a timezone. */
function wallTime(date: Date, tz: string): Wall {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      weekday: 'short',
    });
    formatters.set(tz, f);
  }
  const p: Record<string, string> = {};
  for (const part of f.formatToParts(date)) p[part.type] = part.value;
  return {
    year: Number(p.year),
    month: Number(p.month),
    day: Number(p.day),
    hour: Number(p.hour) % 24,
    minute: Number(p.minute),
    weekday: DAY_NAMES.indexOf((p.weekday ?? '').slice(0, 3).toUpperCase()),
  };
}
const wallKey = (w: Wall) => ((w.year * 100 + w.month) * 100 + w.day) * 10000 + w.hour * 100 + w.minute;

const MINUTE = 60_000;

/**
 * The first time after `after` that matches the schedule in `tz` (null if none within ~5 years).
 * Walks forward in real time and reads the local wall clock, so daylight-saving gaps are skipped and
 * a repeated hour (clocks going back) fires only once.
 */
export function nextRun(spec: CronSpec, tz: string, after: Date): Date | null {
  const afterKey = wallKey(wallTime(after, tz));
  let t = Math.floor(after.getTime() / MINUTE) * MINUTE + MINUTE;
  const horizon = after.getTime() + 5 * 366 * 24 * 60 * MINUTE;
  while (t < horizon) {
    const w = wallTime(new Date(t), tz);
    const minuteOfDay = w.hour * 60 + w.minute;
    const dayOk =
      spec.months.has(w.month) &&
      (spec.domAny && spec.dowAny
        ? true
        : spec.domAny
          ? spec.daysOfWeek.has(w.weekday)
          : spec.dowAny
            ? spec.daysOfMonth.has(w.day)
            : spec.daysOfMonth.has(w.day) || spec.daysOfWeek.has(w.weekday));
    if (!dayOk) {
      // Towards the next local day; stop 2 h early so a daylight-saving change never skips past midnight.
      const left = 24 * 60 - minuteOfDay;
      t += (left > 120 ? left - 120 : 60 - w.minute) * MINUTE;
      continue;
    }
    if (!spec.hours.has(w.hour)) {
      t += (60 - w.minute) * MINUTE;
      continue;
    }
    if (!spec.minutes.has(w.minute) || wallKey(w) <= afterKey) {
      const nextMinute = [...spec.minutes].filter((m) => m > w.minute).sort((a, b) => a - b)[0];
      t += (nextMinute !== undefined ? nextMinute - w.minute : 60 - w.minute) * MINUTE;
      continue;
    }
    return new Date(t);
  }
  return null;
}

/** The next `count` times a schedule fires after `from`. */
export function upcomingRuns(cron: string, tz: string, from: Date, count: number): Date[] {
  const spec = parseCron(cron);
  const out: Date[] = [];
  let t: Date | null = from;
  while (out.length < count && t) {
    t = nextRun(spec, tz, t);
    if (t) out.push(t);
  }
  return out;
}

/** Why a schedule cannot be saved, or null when it is fine. */
export function scheduleProblem(cron: string, tz: string, now = new Date()): string | null {
  if (!isValidTimezone(tz)) return `"${tz}" is not a known timezone`;
  let runs: Date[];
  try {
    runs = upcomingRuns(cron, tz, now, 24);
  } catch (err) {
    return err instanceof CronError ? err.message : 'This schedule is not valid';
  }
  if (runs.length === 0) return 'This schedule never runs';
  for (let i = 1; i < runs.length; i++) {
    if (runs[i]!.getTime() - runs[i - 1]!.getTime() < MIN_SCHEDULE_GAP_MINUTES * MINUTE) {
      return `Schedules can run at most every ${MIN_SCHEDULE_GAP_MINUTES} minutes`;
    }
  }
  return null;
}

// ---------------------------------------------------------------- presets (what the UI offers)

export type SchedulePreset =
  | { kind: 'hourly'; minute: number }
  | { kind: 'daily'; hour: number; minute: number }
  | { kind: 'weekdays'; hour: number; minute: number }
  | { kind: 'weekly'; day: number; hour: number; minute: number }
  | { kind: 'custom'; cron: string };

export function presetToCron(p: SchedulePreset): string {
  switch (p.kind) {
    case 'hourly':
      return `${p.minute} * * * *`;
    case 'daily':
      return `${p.minute} ${p.hour} * * *`;
    case 'weekdays':
      return `${p.minute} ${p.hour} * * 1-5`;
    case 'weekly':
      return `${p.minute} ${p.hour} * * ${p.day}`;
    case 'custom':
      return p.cron.trim();
  }
}

/** The preset a cron expression matches (for editing), or custom. */
export function cronToPreset(cron: string): SchedulePreset {
  const c = cron.trim().split(/\s+/).join(' ');
  const num = '([0-9]|[1-5][0-9])';
  const hr = '([0-9]|1[0-9]|2[0-3])';
  let m = new RegExp(`^${num} \\* \\* \\* \\*$`).exec(c);
  if (m) return { kind: 'hourly', minute: Number(m[1]) };
  m = new RegExp(`^${num} ${hr} \\* \\* \\*$`).exec(c);
  if (m) return { kind: 'daily', minute: Number(m[1]), hour: Number(m[2]) };
  m = new RegExp(`^${num} ${hr} \\* \\* 1-5$`).exec(c);
  if (m) return { kind: 'weekdays', minute: Number(m[1]), hour: Number(m[2]) };
  m = new RegExp(`^${num} ${hr} \\* \\* ([0-6])$`).exec(c);
  if (m) return { kind: 'weekly', minute: Number(m[1]), hour: Number(m[2]), day: Number(m[3]) };
  return { kind: 'custom', cron: c };
}

export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const hhmm = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

/** A schedule in plain words, e.g. "Monday to Friday at 07:30". */
export function describeSchedule(cron: string): string {
  const p = cronToPreset(cron);
  switch (p.kind) {
    case 'hourly':
      return `Every hour at :${String(p.minute).padStart(2, '0')}`;
    case 'daily':
      return `Every day at ${hhmm(p.hour, p.minute)}`;
    case 'weekdays':
      return `Monday to Friday at ${hhmm(p.hour, p.minute)}`;
    case 'weekly':
      return `Every ${WEEKDAY_NAMES[p.day]} at ${hhmm(p.hour, p.minute)}`;
    case 'custom':
      return `Custom schedule (${p.cron})`;
  }
}

// ---------------------------------------------------------------- API contract

const targetSchema = z.object({ type: z.enum(TRIGGER_TARGETS), id: z.string().min(1).max(64) });

const scheduleFields = {
  cron: z.string().trim().max(120).optional(),
  timezone: z.string().trim().max(64).optional(),
};

export const triggerInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    kind: z.enum(TRIGGER_KINDS),
    target: targetSchema,
    enabled: z.boolean().default(true),
    /** When to notify the connected Slack / Teams / Zoho Cliq / email channels. */
    notify: z.enum(['FAILURES', 'ALWAYS', 'OFF']).default('FAILURES'),
    /** Test data collection its runs use (e.g. Staging); null: the default values. */
    collectionId: z.string().min(1).max(64).nullish(),
    ...scheduleFields,
  })
  .superRefine((v, ctx) => {
    if (v.kind !== 'SCHEDULE') return;
    if (!v.cron || !v.timezone) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['cron'], message: 'A schedule needs a time and a timezone' });
      return;
    }
    const problem = scheduleProblem(v.cron, v.timezone);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['cron'], message: problem });
  });
export type TriggerInput = z.infer<typeof triggerInputSchema>;

/** The kind of a trigger cannot change; schedules are re-checked by the API when cron/timezone change. */
export const triggerUpdateSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  target: targetSchema.optional(),
  enabled: z.boolean().optional(),
  notify: z.enum(['FAILURES', 'ALWAYS', 'OFF']).optional(),
  collectionId: z.string().min(1).max(64).nullish(),
  ...scheduleFields,
});
export type TriggerUpdate = z.infer<typeof triggerUpdateSchema>;
